import { ThemedText } from '@/components/themed-text';
import api from '@/services/api';
import { ServiceOccurrence } from '@/types/service-ocurrency';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { EmptyState } from '../ui/empty-state';
import { FilterSelect } from '../ui/filter-select';
import { StaticList } from '../ui/static-list';
import { OccurrenceCard } from './ocurrence-card';

type ServiceOccurrencesTabProps = { serviceId: string | undefined };

const formatDateForAPI = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

/** "8 a 14 de ago" quando cabe no mesmo mês; senão abre os dois lados. */
const formatRangeForDisplay = (start: Date | null, end: Date, isPast: boolean) => {
  const day = (date: Date) => format(date, 'd', { locale: ptBR });
  const dayMonth = (date: Date) => format(date, "d 'de' MMM", { locale: ptBR });

  if (isPast) return `Tudo até ${dayMonth(end)}`;
  if (!start) return 'Período inválido';
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${day(start)} a ${dayMonth(end)}`;
  }
  return `${dayMonth(start)} a ${dayMonth(end)}`;
};
const VERY_OLD_DATE = new Date(1970, 0, 1);
const FAR_FUTURE_DATE = new Date(2100, 11, 31);

type ActiveView = 'past' | 'week' | 'next7' | 'month' | 'nextMonth' | 'all';

/**
 * Opções do filtro, do mais usado para o menos, já com o recorte que cada uma
 * representa — quem abre a lista lê a data, não precisa adivinhar o rótulo.
 */
function buildPeriodOptions(): { value: ActiveView; label: string; hint: string }[] {
  const today = startOfToday();
  const nextMonthStart = startOfMonth(addMonths(today, 1));

  return [
    {
      value: 'next7',
      label: 'Próximos 7 dias',
      hint: formatRangeForDisplay(today, addDays(today, 6), false),
    },
    {
      value: 'week',
      label: 'Esta semana',
      hint: formatRangeForDisplay(
        startOfWeek(today, { weekStartsOn: 1 }),
        endOfWeek(today, { weekStartsOn: 1 }),
        false,
      ),
    },
    {
      value: 'month',
      label: 'Este mês',
      hint: formatRangeForDisplay(startOfMonth(today), endOfMonth(today), false),
    },
    {
      value: 'nextMonth',
      label: 'Mês que vem',
      hint: formatRangeForDisplay(nextMonthStart, endOfMonth(nextMonthStart), false),
    },
    {
      value: 'past',
      label: 'Já aconteceram',
      hint: formatRangeForDisplay(null, subDays(today, 1), true),
    },
    { value: 'all', label: 'Tudo', hint: 'Qualquer data, passada ou futura' },
  ];
}

export function ServiceOccurrencesTab({ serviceId }: ServiceOccurrencesTabProps) {
  const [occurrences, setOccurrences] = useState<ServiceOccurrence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = startOfToday();
  const initialEndDate = addDays(today, 6);
  const [startDate, setStartDate] = useState<Date | null>(today);
  const [endDate, setEndDate] = useState<Date>(initialEndDate);
  const [isPastView, setIsPastView] = useState(false);
  const [activeView, setActiveView] = useState<ActiveView>('next7');

  const fetchOccurrences = useCallback(
    async (start: Date | null, end: Date) => {
      if (!serviceId) return;
      setLoading(true);
      setError(null);
      try {
        const startStr = formatDateForAPI(start ?? VERY_OLD_DATE);
        const endStr = formatDateForAPI(end);

        const response = await api.get<ServiceOccurrence[]>(`service/${serviceId}/occurrences`, {
          params: { startDate: startStr, endDate: endStr },
        });

        const sortedOccurrences = response.data.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          if (dateA === dateB) {
            const timeA = a.startTime || '00:00';
            const timeB = b.startTime || '00:00';
            return isPastView ? timeB.localeCompare(timeA) : timeA.localeCompare(timeB);
          }
          return isPastView ? dateB - dateA : dateA - dateB;
        });
        setOccurrences(sortedOccurrences);
      } catch (err: any) {
        console.error('Failed to fetch occurrences:', err?.response?.data || err?.message || err);
        setError('Sem agendamentos para este serviço');
        setOccurrences([]);
      } finally {
        setLoading(false);
      }
    },
    [serviceId, isPastView],
  );

  useEffect(() => {
    fetchOccurrences(startDate, endDate);
  }, [startDate, endDate, fetchOccurrences]);

  const setThisWeek = () => {
    const today = startOfToday();
    setIsPastView(false);
    setActiveView('week');
    setStartDate(startOfWeek(today, { weekStartsOn: 1 }));
    setEndDate(endOfWeek(today, { weekStartsOn: 1 }));
  };
  const setNext7Days = () => {
    const today = startOfToday();
    setIsPastView(false);
    setActiveView('next7');
    setStartDate(today);
    setEndDate(addDays(today, 6));
  };
  const setThisMonth = () => {
    const today = startOfToday();
    setIsPastView(false);
    setActiveView('month');
    setStartDate(startOfMonth(today));
    setEndDate(endOfMonth(today));
  };
  const setNextMonth = () => {
    const today = startOfToday();
    const nextMonthStart = startOfMonth(addMonths(today, 1));
    setIsPastView(false);
    setActiveView('nextMonth');
    setStartDate(nextMonthStart);
    setEndDate(endOfMonth(nextMonthStart));
  };
  const setPastOccurrences = () => {
    const yesterday = subDays(startOfToday(), 1);
    setIsPastView(true);
    setActiveView('past');
    setStartDate(null);
    setEndDate(yesterday);
  };
  const setAllOccurrences = () => {
    setIsPastView(false);
    setActiveView('all');
    setStartDate(VERY_OLD_DATE);
    setEndDate(FAR_FUTURE_DATE);
  };

  const periodOptions = useMemo(buildPeriodOptions, []);

  const applyView = (view: ActiveView) => {
    if (view === 'past') return setPastOccurrences();
    if (view === 'all') return setAllOccurrences();
    if (view === 'week') return setThisWeek();
    if (view === 'next7') return setNext7Days();
    if (view === 'month') return setThisMonth();
    return setNextMonth();
  };

  const rangeLabel =
    activeView === 'all'
      ? 'Qualquer data'
      : formatRangeForDisplay(startDate, endDate, isPastView);
  const countLabel = loading
    ? 'carregando...'
    : `${occurrences.length} ${occurrences.length === 1 ? 'agendamento' : 'agendamentos'}`;

  return (
    <View className="w-full flex-1 gap-4 px-5 pt-5">
      <FilterSelect
        label="Período"
        sheetTitle="Ver agendamentos de"
        options={periodOptions}
        value={activeView}
        onChange={applyView}
        summary={`${rangeLabel} · ${countLabel}`}
      />

      {loading ? (
        <ActivityIndicator size="large" color="#FD5001" className="mt-10" />
      ) : error ? (
        <ThemedText className="mt-10 text-center text-foreground">{error}</ThemedText>
      ) : occurrences.length === 0 ? (
        <EmptyState
          icon="calendar-outline"
          title="Nada neste período"
          description="Troque o filtro acima para ver outras datas deste serviço."
        />
      ) : (
        <StaticList
          data={occurrences}
          keyExtractor={(item, index) =>
            `${item.enrollmentId || 'no-enrollment'}-${item.date}-${item.startTime || index}`
          }
          renderItem={(item) => <OccurrenceCard occurrence={item} />}
        />
      )}
    </View>
  );
}
